import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getAdminId } from '../utils/getAdminId';
import type { CustomerComplaint, ShopifyOrderLookupResult } from '../types/customerComplaints';

type SortDirection = 'desc' | 'asc';

export function useCustomerComplaints(statusFilter: 'open' | 'resolved') {
  const { user } = useAuth();
  const adminId = getAdminId(user);

  const [complaints, setComplaints] = useState<CustomerComplaint[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const loadComplaints = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_customer_complaints', {
        p_admin_id: adminId,
        p_status_filter: statusFilter,
        p_search: search || null,
        p_sort_direction: sortDirection,
        p_limit: pageSize,
        p_offset: page * pageSize,
      });
      if (error) throw error;
      const rows = (data || []) as CustomerComplaint[];
      setComplaints(rows);
      setTotal(rows.length > 0 ? Number(rows[0].total_count) || 0 : 0);
    } catch {
      setComplaints([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminId, statusFilter, search, sortDirection, page, pageSize]);

  const createComplaint = useCallback(
    async (orderRef: string, shopifyOrderId: number | null, customerName: string | null, text: string) => {
      if (!adminId) return;
      try {
        const { error } = await supabase.rpc('create_customer_complaint', {
          p_admin_id: adminId,
          p_order_reference: orderRef,
          p_shopify_order_id: shopifyOrderId,
          p_customer_name: customerName,
          p_complaint_text: text,
          p_created_by: user?.id ?? adminId,
        });
        if (error) throw error;
        await loadComplaints();
      } catch (err) {
        console.error('Failed to create complaint:', err);
        throw err;
      }
    },
    [adminId, user?.id, loadComplaints],
  );

  const resolveComplaint = useCallback(
    async (complaintId: string, note?: string) => {
      if (!adminId) return;
      try {
        const { error } = await supabase.rpc('resolve_customer_complaint', {
          p_admin_id: adminId,
          p_complaint_id: complaintId,
          p_resolved_by: user?.id ?? adminId,
          p_resolution_note: note ?? null,
        });
        if (error) throw error;
        await loadComplaints();
      } catch (err) {
        console.error('Failed to resolve complaint:', err);
      }
    },
    [adminId, user?.id, loadComplaints],
  );

  const lookupOrder = useCallback(
    async (orderNumber: string): Promise<ShopifyOrderLookupResult[]> => {
      if (!adminId || !orderNumber.trim()) return [];
      try {
        const { data, error } = await supabase.rpc('lookup_shopify_order_customer', {
          p_admin_id: adminId,
          p_order_number: orderNumber.trim(),
        });
        if (error) throw error;
        return (data || []) as ShopifyOrderLookupResult[];
      } catch (err) {
        console.error('Failed to lookup order:', err);
        return [];
      }
    },
    [adminId],
  );

  const updateSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
  }, []);

  const toggleSort = useCallback(() => {
    setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    setPage(0);
  }, []);

  useEffect(() => {
    loadComplaints();
  }, [loadComplaints]);

  return {
    complaints,
    loading,
    total,
    page,
    pageSize,
    search,
    sortDirection,
    setPage,
    updateSearch,
    toggleSort,
    loadComplaints,
    createComplaint,
    resolveComplaint,
    lookupOrder,
  };
}
